import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Site, VismaInvoicePaymentSettings as VismaInvoicePaymentSettingsType } from '@/types';
import { apiService } from '@/utils/api';

const VismaInvoicePayment: React.FC = () => {
  const [formData, setFormData] = useState({
    site_id: '',
    invoice_payment_account_id: '',
    financial_fee_account: '',
    vat_account: '',
    report_recipient: '',
  });

  const [editingSettings, setEditingSettings] = useState<VismaInvoicePaymentSettingsType | null>(null);
  const [siteId, setSiteId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Fetch sites
  const { data: sites = [] } = useQuery<Site[]>('sites', apiService.getSites);

  // Auto-select first site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !siteId) {
      setSiteId(sites[0].id);
      setFormData(prev => ({
        ...prev,
        site_id: sites[0].id.toString(),
      }));
    }
  }, [sites, siteId]);

  // Fetch invoice payment settings
  const { isLoading } = useQuery<VismaInvoicePaymentSettingsType>(
    ['vismaInvoicePaymentSettings', siteId],
    () => apiService.getVismaInvoicePaymentSettings(siteId!),
    {
      enabled: !!siteId,
      onSuccess: (data: VismaInvoicePaymentSettingsType) => {
        setEditingSettings(data);
        setFormData({
          site_id: data.site_id.toString(),
          invoice_payment_account_id: data.invoice_payment_account_id?.toString() || '',
          financial_fee_account: data.financial_fee_account?.toString() || '',
          vat_account: data.vat_account?.toString() || '',
          report_recipient: data.report_recipient || '',
        });
      },
    }
  );

  const createMutation = useMutation(
    (data: VismaInvoicePaymentSettingsType) => apiService.createVismaInvoicePaymentSettings(siteId!, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vismaInvoicePaymentSettings', siteId]);
        alert('Visma Invoice Payment Settings created successfully!');
      },
      onError: () => {
        alert('Failed to create Visma Invoice Payment Settings');
      },
    }
  );

  const updateMutation = useMutation(
    (data: VismaInvoicePaymentSettingsType) => apiService.updateVismaInvoicePaymentSettings(siteId!, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vismaInvoicePaymentSettings', siteId]);
        alert('Visma Invoice Payment Settings updated successfully!');
      },
      onError: () => {
        alert('Failed to update Visma Invoice Payment Settings');
      },
    }
  );

  const deleteMutation = useMutation(
    () => apiService.deleteVismaInvoicePaymentSettings(siteId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vismaInvoicePaymentSettings', siteId]);
        setEditingSettings(null);
        setFormData({
          site_id: '',
          invoice_payment_account_id: '',
          financial_fee_account: '',
          vat_account: '',
          report_recipient: '',
        });
        alert('Visma Invoice Payment Settings deleted successfully!');
      },
      onError: () => {
        alert('Failed to delete Visma Invoice Payment Settings');
      },
    }
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const data: VismaInvoicePaymentSettingsType = {
      site_id: parseInt(formData.site_id),
      invoice_payment_account_id: formData.invoice_payment_account_id ? parseInt(formData.invoice_payment_account_id) : undefined,
      financial_fee_account: formData.financial_fee_account ? parseInt(formData.financial_fee_account) : undefined,
      vat_account: formData.vat_account ? parseInt(formData.vat_account) : undefined,
      report_recipient: formData.report_recipient || undefined,
    };

    if (editingSettings) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete these settings?')) {
      deleteMutation.mutate();
    }
  };

  if (isLoading && siteId) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="section-title">Visma Invoice Payment Settings</h1>
      
      {/* Site Selection */}
      <div className="mb-6">
        <label className="form-label">Site:</label>
        <select
          value={siteId || ''}
          onChange={(e) => {
            const newSiteId = parseInt(e.target.value);
            setSiteId(newSiteId);
            setEditingSettings(null);
            setFormData({
              site_id: e.target.value,
              invoice_payment_account_id: '',
              financial_fee_account: '',
              vat_account: '',
              report_recipient: '',
            });
          }}
          className="form-input"
          disabled={isLoading}
        >
          {sites.map((site: Site) => (
            <option key={site.id} value={site.id}>
              {site.url}
            </option>
          ))}
        </select>
      </div>

      {/* Invoice Payment Settings Form */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="invoice_payment_account_id" className="form-label">
                  Invoice Payment Account
                </label>
                <input
                  type="number"
                  id="invoice_payment_account_id"
                  name="invoice_payment_account_id"
                  value={formData.invoice_payment_account_id}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                  min="1000"
                  max="9999"
                  placeholder="4 digits"
                  required
                />
              </div>

              <div>
                <label htmlFor="financial_fee_account" className="form-label">
                  Financial Fee Account
                </label>
                <input
                  type="number"
                  id="financial_fee_account"
                  name="financial_fee_account"
                  value={formData.financial_fee_account}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                  min="1000"
                  max="9999"
                  placeholder="4 digits"
                  required
                />
              </div>

              <div>
                <label htmlFor="vat_account" className="form-label">
                  VAT Account
                </label>
                <input
                  type="number"
                  id="vat_account"
                  name="vat_account"
                  value={formData.vat_account}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                  min="1000"
                  max="9999"
                  placeholder="4 digits"
                  required
                />
              </div>

              <div>
                <label htmlFor="report_recipient" className="form-label">
                  Report Recipient
                </label>
                <input
                  type="email"
                  id="report_recipient"
                  name="report_recipient"
                  value={formData.report_recipient}
                  onChange={handleInputChange}
                  placeholder="Enter email"
                  className="form-input"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="flex justify-end pt-6 space-x-3">
              <button
                type="submit"
                className="px-6 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                style={{ backgroundColor: 'hsl(15 100% 52%)' }}
                disabled={isLoading || createMutation.isLoading || updateMutation.isLoading}
              >
                {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : (editingSettings ? 'Update' : 'Save')}
              </button>
              
              {editingSettings && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                  disabled={isLoading || deleteMutation.isLoading}
                >
                  {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VismaInvoicePayment;
